import type { Command } from 'commander';
import type { DuoCommand } from '../duo_command';
import type { BackendConfigAdapter } from '../../backend/backend_config_adapter';
import { initDi } from '../../di';
import { parse } from '../../parse';
import { tuiCommandOptionDefs } from '../../command_option_defs';
import { registerOptionsOnCommand } from '../../option_def';
import { getHeadlessEnvInfo } from '../../utils/environment';
import type { ExitHandler } from '../../utils/exit';
import { AcpServer } from './acp_server';

export interface AcpCommandOptions<TBackendOpts> {
  adapter: BackendConfigAdapter<TBackendOpts>;
  version: string;
  exitHandler: ExitHandler;
}

/**
 * Experimental: serves GitLab Duo as an ACP agent over stdio so ACP clients
 * (Zed, JetBrains AI Assistant) can launch `duo acp` as a subprocess. Hidden
 * from `--help` and docs until it has had more real-world testing. Reuses the
 * `tui` option/parse surface. Spec: https://agentclientprotocol.com
 */
export class AcpCommand<TBackendOpts> implements DuoCommand {
  readonly name = 'acp';

  readonly description = 'Start an Agent Client Protocol (ACP) server over stdio (experimental).';

  readonly hidden = true;

  readonly #adapter: BackendConfigAdapter<TBackendOpts>;

  readonly #version: string;

  readonly #exitHandler: ExitHandler;

  constructor(options: AcpCommandOptions<TBackendOpts>) {
    this.#adapter = options.adapter;
    this.#version = options.version;
    this.#exitHandler = options.exitHandler;
  }

  register(node: Command): void {
    registerOptionsOnCommand(node, tuiCommandOptionDefs);
    this.#adapter.registerOptions(node);

    node.action(async () => {
      const allOpts = node.optsWithGlobals();
      const parsed = parse(allOpts, 'tui');
      const backendOpts = this.#adapter.parseOptions(allOpts);

      // stdout is the ACP JSON-RPC channel — never probe the terminal (that
      // would emit escape sequences onto it) and never log there either.
      const envInfo = await getHeadlessEnvInfo();

      const { container } = await initDi(
        { logDestination: 'stderr' },
        parsed,
        this.#version,
        envInfo,
        this.#exitHandler,
        this.#adapter,
        backendOpts,
      );

      const server = container.getRequiredService(AcpServer);
      try {
        await server.serve();
      } finally {
        // The connection is closed — either the client shut us down or it went
        // away and stdin hit EOF, or serve() threw. Nothing keeps serving at
        // this point, but DI services (timers, sockets, child processes) keep
        // the event loop alive indefinitely, so exit explicitly. Running this
        // in `finally` guarantees the single cleanup path even when serve()
        // throws — otherwise Commander would fall back to process.exit(1),
        // bypassing DI disposal and the log flush. The ExitHandler disposes
        // the DI container (it was wired via setDiContainer inside initDi) and
        // flushes logs, same as the TUI and run commands.
        await this.#exitHandler.exit(0);
      }
    });
  }
}
